import React, { Fragment, ReactNode, useCallback, useEffect } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { useIdleTimer } from "react-idle-timer";
import { Transition } from "react-transition-group";
import { useRecoilState, useRecoilValue } from "recoil";
import { Box } from "@mui/material";
import {
  sessionLockedState,
  narrowMenuOpenedState,
  showDrawerTransitionState,
} from "./private-layout-states";
import menuIcon from "@iconify/icons-mdi/menu";
import { InlineIcon } from "@iconify/react";
import { MyHrChangeTemporaryPasswordDialog } from "my-hr/components";
import MyHrUsserSessionLockedPage from "my-hr/pages/user-session-locked-page";
import {
  OcFooter,
  OcRefreshDialog,
  OcSidebar,
  OcSidebarPartnerLogo,
} from "oc/components";
import OcPrivateLayoutAppBar from "oc/components/app-bar";
import { useOcIsMobile, useOcRefreshToken, useOcCurrentUserState } from "oc/hooks";
import { ocActiveMenuItemState } from "oc/states/active-menu-item-state";
import { ocIsTemporaryPasswordState, ocRefreshTokenState, ocSessionPeriodState, twoFaVerificationPendingState } from "states";
import { ocNarrowMenuState } from "states";

interface OcPrivateLayoutProps {
  children: ReactNode;
  onLogout: () => void;
  isLoggedIn: boolean;
}

export const OcPrivateLayout: React.FC<OcPrivateLayoutProps> = ({
  children,
  onLogout,
  isLoggedIn,
}) => {
  const isMobile = useOcIsMobile();
  const history = useHistory();
  const location = useLocation();
  const [sessionLocked, setSessionLocked] = useRecoilState(sessionLockedState);
  const [narrowMenu, setNarrowMenu] = useRecoilState(ocNarrowMenuState);
  const [narrowMenuOpened, setNarrowMenuOpened] = useRecoilState(
    narrowMenuOpenedState,
  );
  const [showDrawerTransition, setShowDrawerTransition] = useRecoilState(
    showDrawerTransitionState,
  );
  const { setData: setCurrentUser } = useOcCurrentUserState();
  const [, setSessionPeriod] = useRecoilState(ocSessionPeriodState);
  const [, setRefreshTokenError] = useRecoilState(ocRefreshTokenState);
  const twoFaVerificationPending = useRecoilValue(twoFaVerificationPendingState);

  // Redirect to login if 2FA verification is pending and user is not on login page
  useEffect(() => {
    if (twoFaVerificationPending && location.pathname !== "/login") {
      history.push("/login");
    }
  }, [twoFaVerificationPending, location.pathname, history]);

  useEffect(() => {
    if (isMobile) setNarrowMenuOpened(false);
  }, [isMobile, setNarrowMenuOpened]);

  const temporaryPassword = useRecoilValue(ocIsTemporaryPasswordState);

  const handleOnIdle = () => {
    if (isLoggedIn) setSessionLocked(true);
  };

  useIdleTimer({
    timeout: 1000 * 60 * 10,
    onIdle: handleOnIdle,
    debounce: 500,
  });

  const handleUnlock = useCallback((loginResult?: any) => {
    if (loginResult?.accessToken) {
      setCurrentUser({
        accessToken: loginResult.accessToken,
        accessTokenPeriod: loginResult.accessTokenPeriod,
      });
      setSessionPeriod(loginResult.sessionPeriod);
      setRefreshTokenError(false);
    }
    setSessionLocked(false);

    // Reload page after unlock to ensure fresh state
    // Small timeout to let Recoil persist the sessionLocked: false state
    setTimeout(() => {
      window.location.reload();
    }, 100);
  }, [setCurrentUser, setSessionPeriod, setRefreshTokenError, setSessionLocked]);

  const [open, setOpen] = React.useState<boolean>(true);

  const handleLogout = () => {
    setSessionLocked(false);
    setNarrowMenu(false);
    setNarrowMenuOpened(false);
    setShowDrawerTransition(false);
    setOpen(false);
    onLogout();
  };

  const handleIconMenuChange = useCallback(
    (state: boolean) => {
      setNarrowMenu(state);
      if (state) setNarrowMenuOpened(false);
    },
    [setNarrowMenu, setNarrowMenuOpened],
  );

  const handleNarrowMenuOpened = (state: boolean) => {
    setNarrowMenuOpened(state);
  };

  useOcRefreshToken({ isLoggedIn });

  const leftMargin = narrowMenu ? "52px" : "310px";
  const duration = 250;

  const defaultStyle = isMobile
    ? undefined
    : {
      transition: `margin-left ${duration}ms ease-in-out`,
      marginLeft: leftMargin,
    };

  const transitionStyles: Record<string, React.CSSProperties> = isMobile
    ? {}
    : {
      entering: { marginLeft: 52 },
      entered: { marginLeft: 52 },
      exiting: { marginLeft: 310 },
      exited: { marginLeft: 310 },
    };

  const measuredRef: React.RefCallback<HTMLDivElement> = useCallback(
    (node) => {
      if (node) {
        const width = node.getBoundingClientRect().width;
        if (width < 600) handleIconMenuChange(true);
      }
    },
    [handleIconMenuChange],
  );

  const handleNarrowMenuToggle = () => {
    setNarrowMenu(!narrowMenu);
  };

  const activeMenu = useRecoilValue(ocActiveMenuItemState);

  useEffect(() => {
    if (isMobile && !activeMenu.endsWith("Collapse")) {
      setNarrowMenu(true);
    }
  }, [activeMenu, isMobile, setNarrowMenu]);

  // Only show private layout (navbar/sidebar) if logged in AND 2FA verification is complete
  const showPrivateLayout = isLoggedIn && !twoFaVerificationPending;

  return (
    <Fragment>
      {showPrivateLayout ? (
        <>
          <OcRefreshDialog />
          <MyHrUsserSessionLockedPage
            sessionLocked={sessionLocked}
            openSession={handleUnlock}
            onLogout={handleLogout}
          />
          <MyHrChangeTemporaryPasswordDialog
            onLogout={onLogout}
            open={temporaryPassword}
          />
          <Box
            ref={measuredRef}
            minHeight={isMobile ? "auto" : "100vh"}
            display="flex"
            flexDirection="row"
            width="100%"
          >
            <Box>
              <OcSidebar
                narrowMenu={narrowMenu}
                onNarrowMenuChange={handleIconMenuChange}
                variant="permanent"
                open={open}
                onNarrowMenuOpened={handleNarrowMenuOpened}
                narrowMenuOpened={narrowMenuOpened}
                showDrawerTransition={showDrawerTransition}
                onLogout={onLogout}
                isMobile={isMobile}
              />
            </Box>
            <Transition in={narrowMenu} timeout={duration}>
              {(state) => (
                <Box
                  id="private-layout-container"
                  display="flex"
                  flexDirection="column"
                  minHeight={isMobile ? "auto" : "100vh"}
                  minWidth={isMobile ? "auto" : "1024px"}
                  width="100%"
                  style={{
                    ...defaultStyle,
                    ...(transitionStyles as any)[state],
                  }}
                >
                  {isMobile ? (
                    <Box
                      sx={{
                        backgroundColor: "#CC0033",
                        width: "auto",
                        height: "64px",
                        p: 1,
                        display: "flex",
                        alignItems: "center",
                        color: "#fff",
                        position: "sticky",
                        top: 0,
                        zIndex: 1000,
                      }}
                    >
                      <OcSidebarPartnerLogo />
                      <InlineIcon
                        icon={menuIcon}
                        height="32px"
                        style={{
                          marginRight: 8,
                          cursor: "pointer",
                        }}
                        onClick={handleNarrowMenuToggle}
                      />
                    </Box>
                  ) : (
                    <OcPrivateLayoutAppBar onLogout={onLogout} />
                  )}
                  {children}
                  <OcFooter />
                </Box>
              )}
            </Transition>
          </Box>
        </>
      ) : (
        <>{children}</>
      )}
    </Fragment>
  );
};

export default OcPrivateLayout;
